import {
  fontWeights,
  GrayGradientDivider,
  Paragraph,
  ParagraphProps,
  paragraphSizes,
  ParagraphWithTooltip,
} from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import classNames from 'classnames';
import React from 'react';
import { RadioInputButton } from '../radio-input-button';

export type RadioInputSectionProps<T> = {
  id?: string;
  wrapperClassName?: string;
  className?: string;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  title: string;
  titleTooltip?: string;
  subtitle?: string;
  selected: boolean;
  open?: boolean;
  value: T;
  info?: string;
  infoProps?: Omit<ParagraphProps, 'children'>;
  onClick: (value: T) => void;
  onHover?: () => void;
  disabled?: boolean;
  infoTooltip?: string;
  subsection?: React.ReactNode;
  elementRef?: React.RefObject<HTMLDivElement | null>;
  selectedColor?: string;
  size?: 'small' | 'large';
  'data-testid'?: string;
};

export function RadioInputSection<T>({
  id,
  infoProps: { className: infoClassName, ...infoProps } = {},
  icon,
  endIcon,
  title,
  subtitle,
  selected,
  open,
  value,
  onClick,
  onHover,
  info,
  wrapperClassName,
  disabled,
  infoTooltip,
  subsection,
  className,
  elementRef,
  titleTooltip,
  selectedColor,
  size = 'large',
  'data-testid': dataTestId,
}: RadioInputSectionProps<T>) {
  return (
    <div ref={elementRef} className={classNames(wrapperClassName)} onMouseEnter={onHover}>
      <div
        className={classNames(
          'bg-white-snow rounded-15',
          { 'hover:bg-gray-concrete': !selected && !disabled },
          className,
        )}
      >
        <TooltipWrapper content={infoTooltip} disabled={!infoTooltip}>
          <button
            id={id}
            className={classNames('flex gap-15px h-72 items-center w-full text-start', {
              'cursor-not-allowed text-gray-dusty!': disabled,
              'cursor-pointer': !disabled,
              'px-18px': size === 'large',
              'p-3 h-60px!': size === 'small',
              className,
            })}
            onClick={() => {
              if (disabled) return;
              onClick(value);
            }}
            data-testid={dataTestId}
          >
            {icon && <div className='shrink-0'>{icon}</div>}
            <div className='overflow-hidden'>
              <ParagraphWithTooltip
                className={classNames({ 'underline-dashed': titleTooltip })}
                weight={fontWeights.medium}
                tooltipProps={{ content: titleTooltip, disabled: !titleTooltip, interactive: true }}
              >
                {title}
              </ParagraphWithTooltip>

              {subtitle && (
                <ParagraphWithTooltip
                  weight={fontWeights.medium}
                  size={paragraphSizes.tiny}
                  colorClassName='text-gray-devilSolid'
                >
                  {subtitle}
                </ParagraphWithTooltip>
              )}
            </div>
            <div className='ml-auto shrink-0'>
              {endIcon ?? <RadioInputButton className='w-6 h-6' selected={selected} selectedColor={selectedColor} />}
            </div>
          </button>
        </TooltipWrapper>

        {subsection && (selected || open) && (
          <>
            <GrayGradientDivider className={classNames({ 'my-3': size === 'large' })} />
            <div className='p-2'>{subsection}</div>
          </>
        )}
      </div>
      {info && (
        <Paragraph
          colorClassName='text-gray-devil'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
          className={classNames('truncate mt-1 ml-5', infoClassName)}
          {...infoProps}
        >
          {info}
        </Paragraph>
      )}
    </div>
  );
}
